import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { DoorOpen, LoaderCircle, UserX } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { entryState, type PendingEntryRequest } from "./entry-state"
import { useEntryRequestWatch } from "./use-entry-request-watch"
import { t } from "@/lib/i18n"

type EntryAskPanelProps = {
  token: string
  maxNameLength: number
  pendingRequest: PendingEntryRequest | null
}

// Read straight off the page's own props rather than threaded through as a
// component prop: `EntryAskPanel` only ever renders on `user/invites/menu`,
// and `User::Invites::MenusController#show` already puts `occupied` on the
// same `entry` object this page passes everything else from. Same pattern as
// `DinerRealtimeProvider` reading `usePage<{ user: ... }>().props`.
type EntryPageProps = {
  entry?: { occupied?: boolean }
}

/**
 * The one action the pre-seating menu offers: ask for the table to be opened.
 * Pinned under the menu rather than parked behind a link — a person standing
 * at an unmoving QR is the whole reason this exists, and the door has to be
 * as easy to find as the food.
 *
 * `entryState` decides which of the three faces this shows: the ask itself,
 * "waiting" once it has been sent (with the watch that notices an approval
 * landing in someone else's browser), or "refused" when a person already said
 * no. A refusal offers no form back — see `entry-state.ts`.
 */
export function EntryAskPanel({ token, maxNameLength, pendingRequest }: EntryAskPanelProps) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const { waiting, refused, canAsk } = entryState({ request: pendingRequest })
  const { entry } = usePage<EntryPageProps>().props
  const occupied = entry?.occupied ?? false

  useEntryRequestWatch({
    enabled: waiting,
    path: `/invites/${token}`,
    expiresInSeconds: pendingRequest?.expiresInSeconds ?? null,
  })

  const askToEnter = () => {
    if (submitting) return

    setSubmitting(true)
    router.post(
      `/invites/${token}/entry_requests`,
      { requester_name: name },
      // `replace`: the ask is the point of no return on this screen, and the
      // screen behind it is the pre-seating menu of the same printed QR. Left
      // as a push, it sits under every later entry for the rest of the visit,
      // and a diner who is by then seated and pressing back lands on "ask for
      // the table to be opened" — the door they already came through, offering
      // itself again. Replacing it costs reading the menu while waiting; the
      // waiting screen is where this flow means to put them anyway.
      { replace: true, onSuccess: () => setOpen(false), onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <div
      data-testid="entry-ask-panel"
      className="sticky bottom-0 mt-auto border-t bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/75"
    >
      {waiting ? (
        <p
          className="flex h-11 items-center justify-center gap-2 text-sm font-medium text-muted-foreground md:h-9"
          data-testid="entry-ask-waiting"
        >
          <LoaderCircle className="size-4 shrink-0 animate-spin" />
          {t("ui.react.pages.user.invites.waiting.title")}
        </p>
      ) : refused ? (
        <p
          className="flex h-11 items-center justify-center gap-2 text-sm font-medium text-warning md:h-9"
          data-testid="entry-ask-refused"
        >
          <UserX className="size-4 shrink-0" />
          {t("ui.react.pages.user.invites.refused.title")}
        </p>
      ) : (
        <Button
          size="lg"
          className="w-full"
          onClick={() => setOpen(true)}
          disabled={!canAsk}
          data-testid="entry-ask-open"
        >
          <DoorOpen />
          {occupied
            ? t("ui.react.pages.user.invites.menu.ask_action_occupied")
            : t("ui.react.pages.user.invites.menu.ask_action")}
        </Button>
      )}

      <ResponsiveDrawer open={open} onOpenChange={setOpen}>
        <DrawerContent data-testid="entry-ask-sheet">
          <DrawerHeader>
            <DrawerTitle>{t("ui.react.pages.user.invites.ask.title")}</DrawerTitle>
            <DrawerDescription>{t("ui.react.pages.user.invites.ask.explanation")}</DrawerDescription>
          </DrawerHeader>

          <div className="space-y-2 px-4 pb-2">
            <Label htmlFor="entry-ask-name">{t("ui.react.pages.user.invites.ask.label")}</Label>
            <Input
              id="entry-ask-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={maxNameLength}
              autoComplete="given-name"
              placeholder={t("ui.react.pages.user.invites.ask.placeholder")}
              data-testid="entry-ask-name-input"
            />
          </div>

          <DrawerFooter>
            <Button
              className="w-full"
              onClick={askToEnter}
              disabled={name.trim().length === 0 || submitting}
              data-testid="entry-ask-submit"
            >
              {t("ui.react.pages.user.invites.ask.action")}
            </Button>
            <DrawerClose asChild>
              <Button type="button" variant="outline" className="w-full">
                {t("ui.common.actions.cancel")}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </ResponsiveDrawer>
    </div>
  )
}
